// src/providers/postgrestDataProvider.ts
import type { DataProvider, CustomRequest, HttpError } from "@refinedev/core";
import { RESOURCES } from "../resources/config";
import type { ResourceField } from "../resources/resourceTypes";

/* ------------------------------------------------------------------ */
/* helpers                                                            */
/* ------------------------------------------------------------------ */

const getResourceConfig = (resource: string) =>
  RESOURCES.find((r) => r.name === resource);

// prefer meta.profile → meta.schema → resource cfg → public
const resolveSchema = (
  resource: string | undefined,
  meta: any | undefined,
): string => {
  const cfg = resource ? getResourceConfig(resource) : undefined;
  return meta?.profile ?? meta?.schema ?? cfg?.schema ?? "public";
};

const buildHeaders = (schema: string, token?: string) => {
  const h: Record<string, string> = {
    "Content-Type": "application/json",
    "Accept-Profile": schema,
    "Content-Profile": schema,
    Prefer: "return=representation,count=exact",
  };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

async function toHttpError(res: Response): Promise<HttpError> {
  let body: any = null;
  try {
    body = await res.json();
  } catch {}
  return {
    message: body?.message || res.statusText || "Request failed",
    statusCode: res.status,
    body,
  };
}

// keep OR expressions parseable
const sanitizeForOr = (v: unknown) =>
  String(v ?? "")
    .replace(/[(),]/g, " ")
    .trim();

const looksLikeUuid = (s: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    s.trim(),
  );

const looksLikeNumber = (s: string) => /^-?\d+(\.\d+)?$/.test(s.trim());

const looksLikeIpOrCidr = (s: string) => {
  const q = s.trim();

  // ipv4 (optionally /cidr)
  const ipv4 =
    /^(?:\d{1,3}\.){3}\d{1,3}(?:\/\d{1,2})?$/.test(q) &&
    q
      .split("/")[0]
      .split(".")
      .every((o) => {
        const n = Number(o);
        return Number.isInteger(n) && n >= 0 && n <= 255;
      });

  // rough ipv6 (optionally /cidr)
  const ipv6 = /^[0-9a-fA-F:]+(?:\/\d{1,3})?$/.test(q) && q.includes(":");

  return ipv4 || ipv6;
};

const isCidrInput = (s: string) => s.trim().includes("/");

const fieldKindMap = (fields: ResourceField[]) => {
  const m = new Map<string, ResourceField["kind"]>();
  for (const f of fields) m.set(f.source, f.kind);
  return m;
};

const inferInetFields = (cfg: any): string[] => {
  if (Array.isArray(cfg?.inetSearchFields) && cfg.inetSearchFields.length) {
    return cfg.inetSearchFields;
  }
  return (cfg?.fields || [])
    .filter((f: any) => f?.kind === "inet")
    .map((f: any) => f.source);
};

const inferCidrFields = (cfg: any): string[] => {
  return (cfg?.fields || [])
    .filter((f: any) => f?.kind === "cidr")
    .map((f: any) => f.source);
};

/* ------------------------------------------------------------------ */
/* provider                                                           */
/* ------------------------------------------------------------------ */

export const postgrestDataProvider = (
  apiUrl: string,
  getToken?: () => string | undefined,
): DataProvider => {
  const resourceUrl = (resource: string) =>
    `${apiUrl.replace(/\/$/, "")}/${resource}`;

  const buildQueryString = (
    resource: string,
    {
      pagination,
      filters,
      sorters,
    }: { pagination?: any; filters?: any[]; sorters?: any[] },
  ) => {
    const cfg: any = getResourceConfig(resource);
    const qs = new URLSearchParams();

    /* ---------------- pagination ---------------- */
    if (pagination) {
      const { current = 1, pageSize = 10 } = pagination;
      qs.set("limit", String(pageSize));
      qs.set("offset", String((current - 1) * pageSize));
    }

    /* ---------------- sorting ---------------- */
    if (sorters?.length) {
      const s = sorters[0];
      qs.set("order", `${s.field}.${s.order}`);
    }

    /* ---------------- filters ---------------- */
    if (filters?.length) {
      // ONE global filter only: field "search" or "q" (GenericCrud should send this)
      const global = filters.find(
        (f: any) =>
          f &&
          f.operator === "contains" &&
          (f.field === "search" ||
            f.field === "q" ||
            f.field === "search_text"),
      );

      // everything else = normal eq filters
      const normal = filters.filter((f: any) => f !== global);

      for (const f of normal) {
        if (!f?.field) continue;
        qs.set(f.field, `eq.${f.value}`);
      }

      if (global) {
        const raw = sanitizeForOr(global.value);
        if (!raw) return qs.toString();

        const parts: string[] = [];
        const kinds = fieldKindMap(cfg?.fields || []);

        // --- TEXT SEARCH (ilike) ONLY FOR TEXT-LIKE FIELDS ---
        const textFields: string[] = Array.isArray(cfg?.searchFields)
          ? cfg.searchFields
          : [];
        for (const field of textFields) {
          const k = kinds.get(field);
          if (
            k &&
            [
              "inet",
              "cidr",
              "number",
              "date",
              "boolean",
              "uuid",
              "select",
            ].includes(k)
          )
            continue;
          parts.push(`${field}.ilike.*${raw}*`);
        }

        // --- ARRAY SEARCH (cs.{term}) ---
        const arrFields: string[] = Array.isArray(cfg?.arraySearchFields)
          ? cfg.arraySearchFields
          : [];
        for (const field of arrFields) {
          parts.push(`${field}.cs.{${raw}}`);
        }

        // --- UUID / NUMBER typed fallbacks if user typed a uuid/number ---
        if (looksLikeUuid(raw)) {
          for (const f of (cfg?.fields || []) as ResourceField[]) {
            if (f.kind === "uuid") parts.push(`${f.source}.eq.${raw}`);
          }
        }

        if (looksLikeNumber(raw)) {
          for (const f of (cfg?.fields || []) as ResourceField[]) {
            if (f.kind === "number") parts.push(`${f.source}.eq.${raw}`);
          }
        }

        // --- INET/CIDR SEARCH ---
        if (looksLikeIpOrCidr(raw)) {
          const inetFields = inferInetFields(cfg);
          const cidrFields = inferCidrFields(cfg);
          const isNet = isCidrInput(raw);

          for (const f of inetFields) {
            if (isNet)
              parts.push(`${f}.sl.${raw}`); // "<<"
            else parts.push(`${f}.eq.${raw}`);
          }

          for (const f of cidrFields) {
            if (isNet) {
              parts.push(`${f}.ov.${raw}`); // "&&"
              parts.push(`${f}.eq.${raw}`);
              parts.push(`${f}.sl.${raw}`); // cidr << cidr
              parts.push(`${f}.sr.${raw}`); // cidr >> cidr
            } else {
              parts.push(`${f}.sr.${raw}`); // ">>" contains IP
            }
          }
        }

        if (parts.length) qs.set("or", `(${parts.join(",")})`);
      }
    }

    return qs.toString();
  };

  return {
    getList: async ({ resource, pagination, filters, sorters, meta }) => {
      const schema = resolveSchema(resource, meta);
      const token = getToken?.();

      const query = buildQueryString(resource, {
        pagination,
        filters,
        sorters,
      });
      const url = query
        ? `${resourceUrl(resource)}?${query}`
        : resourceUrl(resource);

      const res = await fetch(url, {
        method: "GET",
        headers: buildHeaders(schema, token),
      });
      if (!res.ok) throw await toHttpError(res);

      const data = await res.json();
      const cr = res.headers.get("content-range");
      const total = cr ? Number(cr.split("/")[1]) : data.length;

      return { data, total };
    },

    getOne: async ({ resource, id, meta }) => {
      const schema = resolveSchema(resource, meta);
      const token = getToken?.();

      const res = await fetch(`${resourceUrl(resource)}?id=eq.${id}`, {
        method: "GET",
        headers: buildHeaders(schema, token),
      });
      if (!res.ok) throw await toHttpError(res);

      const data = await res.json();
      return { data: data[0] };
    },

    create: async ({ resource, variables, meta }) => {
      const schema = resolveSchema(resource, meta);
      const token = getToken?.();

      const res = await fetch(resourceUrl(resource), {
        method: "POST",
        headers: buildHeaders(schema, token),
        body: JSON.stringify(variables),
      });
      if (!res.ok) throw await toHttpError(res);

      const data = await res.json();
      return { data: Array.isArray(data) ? data[0] : data };
    },

    update: async ({ resource, id, variables, meta }) => {
      const schema = resolveSchema(resource, meta);
      const token = getToken?.();

      const res = await fetch(`${resourceUrl(resource)}?id=eq.${id}`, {
        method: "PATCH",
        headers: buildHeaders(schema, token),
        body: JSON.stringify(variables),
      });
      if (!res.ok) throw await toHttpError(res);

      const data = await res.json();
      return { data: Array.isArray(data) ? data[0] : data };
    },

    deleteOne: async ({ resource, id, meta }) => {
      const schema = resolveSchema(resource, meta);
      const token = getToken?.();

      const res = await fetch(`${resourceUrl(resource)}?id=eq.${id}`, {
        method: "DELETE",
        headers: buildHeaders(schema, token),
      });
      if (!res.ok) throw await toHttpError(res);

      const data = await res.json().catch(() => ({}));
      return { data };
    },

    getMany: async ({ resource, ids, meta }) => {
      const schema = resolveSchema(resource, meta);
      const token = getToken?.();

      const res = await fetch(
        `${resourceUrl(resource)}?id=in.(${ids.join(",")})`,
        {
          method: "GET",
          headers: buildHeaders(schema, token),
        },
      );
      if (!res.ok) throw await toHttpError(res);

      const data = await res.json();
      return { data };
    },

    // ✅ IMPORTANT: relative URLs must go to apiUrl (PostgREST), not the frontend origin
    custom: async ({
      url,
      method = "GET",
      headers = {},
      data,
      resource,
      meta,
    }: CustomRequest) => {
      const schema = resolveSchema(resource, meta);
      const token = getToken?.();

      if (!url) throw new Error("custom: url is required");

      const base = apiUrl.replace(/\/$/, "");
      const fullUrl = /^https?:\/\//i.test(url)
        ? url
        : url.startsWith("/")
          ? `${base}${url}`
          : `${base}/${url}`;

      const res = await fetch(fullUrl, {
        method,
        headers: { ...buildHeaders(schema, token), ...headers },
        body:
          data && method !== "GET" && method !== "HEAD"
            ? JSON.stringify(data)
            : undefined,
      });
      if (!res.ok) throw await toHttpError(res);

      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) return { data: await res.json() };

      return { data: await res.blob() };
    },

    getApiUrl: () => apiUrl,
  };
};
